import { useState, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { updatePassword } from 'aws-amplify/auth';
import { useAuth } from '@/hooks/useAuth';
import { useProfile, useUpdateProfile } from '@/hooks/useProfile';
import { useOrders } from '@/hooks/useOrders';
import { useQuotes } from '@/hooks/useQuote';
import { useToast } from '@/contexts/ToastContext';
import { profileApi } from '@/services/profile';
import { Button } from '@/components/ui/Button';
import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

export const ProfilePage = () => {
  const { user, isAdmin, signOut } = useAuth();
  const { data: profile, isLoading } = useProfile();
  const updateProfile = useUpdateProfile();
  const { data: ordersData } = useOrders();
  const { data: quotesData } = useQuotes();
  const toast = useToast();
  const navigate = useNavigate();

  const [isEditingName, setIsEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const handleEditName = useCallback(() => {
    setNameValue(profile?.name ?? '');
    setIsEditingName(true);
  }, [profile?.name]);

  const handleSaveName = useCallback(() => {
    const trimmed = nameValue.trim();
    if (!trimmed) {
      toast.error('Name cannot be empty');
      return;
    }
    updateProfile.mutate(
      { name: trimmed },
      {
        onSuccess: () => {
          setIsEditingName(false);
          toast.success('Name updated');
        },
        onError: () => toast.error('Failed to update name'),
      },
    );
  }, [nameValue, updateProfile, toast]);

  const handleCancelEdit = useCallback(() => {
    setIsEditingName(false);
    setNameValue('');
  }, []);

  const handleChangePassword = useCallback(async () => {
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 12) {
      toast.error('Password must be at least 12 characters');
      return;
    }
    if (!/[a-z]/.test(newPassword)) {
      toast.error('Password must include a lowercase letter');
      return;
    }
    if (!/[A-Z]/.test(newPassword)) {
      toast.error('Password must include an uppercase letter');
      return;
    }
    if (!/\d/.test(newPassword)) {
      toast.error('Password must include a number');
      return;
    }
    if (!/[^a-zA-Z0-9]/.test(newPassword)) {
      toast.error('Password must include a symbol');
      return;
    }

    setIsChangingPassword(true);
    try {
      await updatePassword({
        oldPassword: currentPassword,
        newPassword,
      });
      toast.success('Password changed successfully');
      setShowPasswordForm(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to change password';
      toast.error(message);
    } finally {
      setIsChangingPassword(false);
    }
  }, [currentPassword, newPassword, confirmPassword, toast]);

  const handleDeleteAccount = useCallback(async () => {
    setIsDeletingAccount(true);
    try {
      await profileApi.deleteAccount();
      setShowDeleteConfirm(false);
      await signOut();
      navigate('/');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete account';
      toast.error(message);
      setIsDeletingAccount(false);
    }
  }, [signOut, navigate, toast]);

  const orderCount = ordersData?.data.length ?? 0;
  const quoteCount = quotesData?.data.length ?? 0;

  if (isLoading) {
    return (
      <div
        className="container-main"
        style={{
          maxWidth: '640px',
          paddingTop: 'var(--space-12)',
          paddingBottom: 'var(--space-12)',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div
      className="container-main"
      style={{
        maxWidth: '640px',
        paddingTop: 'var(--space-12)',
        paddingBottom: 'var(--space-12)',
      }}
    >
      <h1
        style={{
          fontSize: 'var(--text-3xl)',
          fontWeight: 'var(--font-weight-bold)',
          color: 'var(--text-primary)',
          letterSpacing: 'var(--tracking-tight)',
          marginBottom: 'var(--space-8)',
        }}
      >
        My Profile
      </h1>

      {/* Profile info card */}
      <div
        style={{
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border-default)',
          backgroundColor: 'var(--bg-surface)',
          padding: 'var(--space-6)',
          marginBottom: 'var(--space-6)',
        }}
      >
        {/* Avatar + name */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', marginBottom: 'var(--space-6)' }}>
          <div
            style={{
              width: '56px',
              height: '56px',
              borderRadius: 'var(--radius-full)',
              backgroundColor: 'var(--color-primary-600)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 'var(--text-xl)',
              fontWeight: 'var(--font-weight-bold)',
              flexShrink: 0,
            }}
          >
            {(profile?.name?.[0] ?? user?.email?.[0] ?? '?').toUpperCase()}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <h2
                style={{
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--text-primary)',
                }}
              >
                {profile?.name || 'No name set'}
              </h2>
              <button
                type="button"
                onClick={handleEditName}
                aria-label="Edit name"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--text-tertiary)',
                  padding: 'var(--space-1)',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </button>
            </div>
            <p
              style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--text-tertiary)',
                marginTop: 'var(--space-1)',
                wordBreak: 'break-all',
              }}
            >
              {user?.email}
            </p>
          </div>
        </div>

        {/* Info rows */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-3)',
            borderTop: '1px solid var(--border-default)',
            paddingTop: 'var(--space-4)',
          }}
        >
          <InfoRow label="Member since" value={profile?.createdAt ? new Date(profile.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '--'} />
          <InfoRow label="User ID" value={profile?.userId ?? '--'} mono />
        </div>
      </div>

      {/* Navigation links */}
      <div
        style={{
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border-default)',
          backgroundColor: 'var(--bg-surface)',
          overflow: 'hidden',
          marginBottom: 'var(--space-6)',
        }}
      >
        <NavRow
          to="/orders"
          icon={
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
              <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
              <line x1="12" y1="22.08" x2="12" y2="12" />
            </svg>
          }
          label="My Orders"
          description="View order history and track shipments"
          count={orderCount}
        />
        <NavRow
          to="/quotes"
          icon={
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </svg>
          }
          label="My Quotes"
          description="View saved quotes and pricing"
          count={quoteCount}
          last={!isAdmin}
        />
        {isAdmin && (
          <NavRow
            to="/admin"
            icon={
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" />
                <rect x="14" y="3" width="7" height="7" />
                <rect x="14" y="14" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" />
              </svg>
            }
            label="Admin Dashboard"
            description="Manage orders, products, and settings"
            count={0}
            last
          />
        )}
      </div>

      {/* Account */}
      <div
        style={{
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border-default)',
          backgroundColor: 'var(--bg-surface)',
          padding: 'var(--space-6)',
        }}
      >
        <h3
          style={{
            fontSize: 'var(--text-base)',
            fontWeight: 'var(--font-weight-semibold)',
            color: 'var(--text-primary)',
            marginBottom: 'var(--space-4)',
          }}
        >
          Account
        </h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
          <Button variant="secondary" onClick={() => setShowPasswordForm(true)}>
            Change Password
          </Button>
          <Button variant="secondary" onClick={() => signOut()}>
            Sign Out
          </Button>
          <Button variant="danger" onClick={() => setShowDeleteConfirm(true)}>
            Delete My Account
          </Button>
        </div>
      </div>

      {showPasswordForm && (
        <ChangePasswordModal
          currentPassword={currentPassword}
          newPassword={newPassword}
          confirmPassword={confirmPassword}
          onCurrentChange={setCurrentPassword}
          onNewChange={setNewPassword}
          onConfirmChange={setConfirmPassword}
          onSave={handleChangePassword}
          onClose={() => {
            setShowPasswordForm(false);
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
          }}
          isPending={isChangingPassword}
        />
      )}

      {showDeleteConfirm && (
        <ConfirmModal
          title="Delete your account?"
          message="This will permanently delete your account, quotes, and uploaded files. Order history is retained for financial records. This action cannot be undone."
          confirmLabel="Delete Everything"
          cancelLabel="Keep Account"
          onConfirm={handleDeleteAccount}
          onClose={() => setShowDeleteConfirm(false)}
          isPending={isDeletingAccount}
        />
      )}

      {isEditingName && (
        <EditNameModal
          value={nameValue}
          onChange={setNameValue}
          onSave={handleSaveName}
          onClose={handleCancelEdit}
          isPending={updateProfile.isPending}
        />
      )}
    </div>
  );
};

const InfoRow = ({ label, value, mono }: { label: string; value: string; mono?: boolean }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <span
      style={{
        fontSize: 'var(--text-sm)',
        color: 'var(--text-tertiary)',
      }}
    >
      {label}
    </span>
    <span
      style={{
        fontSize: 'var(--text-sm)',
        color: 'var(--text-primary)',
        fontFamily: mono ? 'var(--font-mono, monospace)' : 'inherit',
        maxWidth: '60%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        textAlign: 'right',
      }}
    >
      {value}
    </span>
  </div>
);

const NavRow = ({
  to,
  icon,
  label,
  description,
  count,
  last,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  description: string;
  count: number;
  last?: boolean;
}) => (
  <Link
    to={to}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--space-4)',
      padding: 'var(--space-4) var(--space-5)',
      textDecoration: 'none',
      borderBottom: last ? 'none' : '1px solid var(--border-default)',
      transition: 'background-color var(--duration-normal) var(--ease-out)',
    }}
    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--bg-surface-sunken)'; }}
    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
  >
    <div style={{ color: 'var(--text-tertiary)', flexShrink: 0 }}>
      {icon}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div
        style={{
          fontSize: 'var(--text-base)',
          fontWeight: 'var(--font-weight-medium)',
          color: 'var(--text-primary)',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 'var(--text-sm)',
          color: 'var(--text-tertiary)',
          marginTop: '2px',
        }}
      >
        {description}
      </div>
    </div>
    {count > 0 && (
      <span
        style={{
          backgroundColor: 'var(--color-primary-600)',
          color: '#fff',
          fontSize: 'var(--text-xs)',
          fontWeight: 'var(--font-weight-semibold)',
          borderRadius: 'var(--radius-full)',
          padding: '2px 8px',
          minWidth: '20px',
          textAlign: 'center',
          flexShrink: 0,
        }}
      >
        {count}
      </span>
    )}
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--text-tertiary)"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0 }}
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  </Link>
);

const EditNameModal = ({
  value,
  onChange,
  onSave,
  onClose,
  isPending,
}: {
  value: string;
  onChange: (v: string) => void;
  onSave: () => void;
  onClose: () => void;
  isPending: boolean;
}) => {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'var(--bg-surface)',
          borderRadius: 'var(--radius-lg)',
          padding: '20px 24px',
          width: '360px',
          maxWidth: '90%',
          boxShadow: 'var(--shadow-lg)',
        }}
      >
        <h3
          style={{
            fontSize: '16px',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--text-primary)',
            marginBottom: '12px',
          }}
        >
          Edit Name
        </h3>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onSave();
          }}
          autoFocus
          style={{
            width: '100%',
            padding: 'var(--space-2) var(--space-3)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border-default)',
            backgroundColor: 'var(--bg-page)',
            color: 'var(--text-primary)',
            fontSize: 'var(--text-sm)',
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
        <div className="flex justify-end" style={{ gap: '8px', marginTop: '16px' }}>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button size="sm" onClick={onSave} disabled={isPending || !value.trim()}>
            {isPending ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const ChangePasswordModal = ({
  currentPassword,
  newPassword,
  confirmPassword,
  onCurrentChange,
  onNewChange,
  onConfirmChange,
  onSave,
  onClose,
  isPending,
}: {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
  onCurrentChange: (v: string) => void;
  onNewChange: (v: string) => void;
  onConfirmChange: (v: string) => void;
  onSave: () => void;
  onClose: () => void;
  isPending: boolean;
}) => {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'var(--bg-surface)',
          borderRadius: 'var(--radius-lg)',
          padding: '20px 24px',
          width: '360px',
          maxWidth: '90%',
          boxShadow: 'var(--shadow-lg)',
        }}
      >
        <h3
          style={{
            fontSize: '16px',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--text-primary)',
            marginBottom: '16px',
          }}
        >
          Change Password
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <PasswordInput label="Current password" value={currentPassword} onChange={onCurrentChange} />
          <PasswordInput label="New password" value={newPassword} onChange={onNewChange} hint="12+ characters with uppercase, lowercase, number, and symbol" />
          <PasswordInput label="Confirm new password" value={confirmPassword} onChange={onConfirmChange} />
        </div>
        <div className="flex justify-end" style={{ gap: '8px', marginTop: '16px' }}>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button size="sm" onClick={onSave} disabled={isPending || !currentPassword || !newPassword || !confirmPassword}>
            {isPending ? 'Changing...' : 'Change Password'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const PasswordInput = ({
  label,
  value,
  onChange,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
}) => (
  <div>
    <label
      style={{
        display: 'block',
        fontSize: 'var(--text-sm)',
        fontWeight: 'var(--font-weight-medium)',
        color: 'var(--text-secondary)',
        marginBottom: 'var(--space-1)',
      }}
    >
      {label}
    </label>
    <input
      type="password"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: '100%',
        padding: 'var(--space-2) var(--space-3)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border-default)',
        backgroundColor: 'var(--bg-page)',
        color: 'var(--text-primary)',
        fontSize: 'var(--text-sm)',
        outline: 'none',
        boxSizing: 'border-box',
      }}
    />
    {hint && (
      <p
        style={{
          fontSize: 'var(--text-xs)',
          color: 'var(--text-tertiary)',
          marginTop: '4px',
        }}
      >
        {hint}
      </p>
    )}
  </div>
);
